import useNotificationHandler from "@/hooks/useNotificationHandler";
import {
  useAllNotifications,
  useMarkNotificationViewed,
  useUpdateNotification,
} from "@/hooks/useNotifications";
import { INotification } from "@/interfaces/notification.interface";
import { IProject } from "@/interfaces/project.interface";
import { useAppSelector } from "@/store/hooks/hooks";
import { setSelectedProject } from "@/store/slices/projectSlice";
import { RootState } from "@/store/store";
import { notificationFilters } from "@/utils/kendo";
import { Button } from "@progress/kendo-react-buttons";
import { Drawer } from "@progress/kendo-react-layout";
import { Bell, BellDot, BellOff, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

interface NotificationProps {
  notification: INotification;
  onDelete: (id?: string) => void;
  onClick: (notification: INotification) => void; // Added click handler
}

const NotificationItem = ({
  notification,
  onDelete,
  onClick,
}: NotificationProps) => {
  return (
    <div
      className="flex gap-3 px-3 py-4 border-b group cursor-pointer"
      onClick={() => onClick(notification)}
    >
      {notification.isViewed ? (
        <Bell className="w-6 h-6" />
      ) : (
        <BellDot className="w-6 h-6 text-primary" />
      )}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-neutral-160 line-clamp-1">
            {notification.title}
          </h4>
          <button
            className="hidden group-hover:inline-flex items-center justify-center h-6 w-6 relative"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(notification.id);
            }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-neutral-130 line-clamp-1 break-all">
          {notification.message}
        </p>
      </div>
    </div>
  );
};

const Alert = ({ color }: any) => {
  const dispatch = useDispatch();

  const markTaskViewedMutation = useMarkNotificationViewed();
  const updateNotificationMutation = useUpdateNotification();
  const { user } = useAppSelector((state: RootState) => state.user);
  const { projects, selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );
  const navigate = useNavigate();

  const [visible, setVisible] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasUnreadNotification, setHasUnreadNotification] =
    useState<boolean>(false);

  const filterParams = useMemo(
    () => ({ filter: notificationFilters(user?.id) }),
    [user]
  );
  const { data: notificationsData } = useAllNotifications(
    { ...filterParams, sort: [{ field: "CreatedOn", dir: "desc" }] },
    { showLoader: false }
  );

  const { handleNotificationClick } = useNotificationHandler(
    () => {},
    () => {},
    { current: null }
  );

  useEffect(() => {
    setNotifications(notificationsData?.data || []);
  }, [notificationsData]);

  useEffect(() => {
    if (notifications?.length && user?.id) {
      setHasUnreadNotification(notifications.some((item) => !item.isViewed));
    }
  }, [notifications]);

  useEffect(() => {
    if (!visible && hasUnreadNotification && user?.id) {
      setHasUnreadNotification(false);
      setNotifications(notifications.map((x) => ({ ...x, isViewed: true })));
      markTaskViewedMutation.mutate(user.id);
    }
  }, [visible]);

  const handleDelete = (id?: string) => {
    const notification = notifications.find((x) => x.id === id);
    if (!notification || !id) return;

    setNotifications(notifications.filter((x) => x.id !== id));
    updateNotificationMutation.mutate({
      id,
      data: { ...notification, isActive: false },
    });
  };

  const handleNotificationItemClick = (notification: INotification) => {
    try {
      const additionalData = notification.additionalData
        ? JSON.parse(notification.additionalData)
        : {};
      const { areaId, levelId, projectId } = additionalData;

      if (areaId && levelId && projectId) {
        if (selectedProject?.id !== projectId) {
          const project = projects.find((p) => p.id === projectId);
          dispatch(setSelectedProject(project as IProject));
        }

        if (window.location.pathname !== "/home") {
          navigate("/home");

          setTimeout(() => {
            navigate(
              `/home?areaId=${areaId}&levelId=${levelId}&projectId=${projectId}`
            );

            window.dispatchEvent(
              new CustomEvent("open-details-drawer", {
                detail: { areaId, levelId, projectId },
              })
            );
          }, 500);
        } else {
          window.dispatchEvent(
            new CustomEvent("open-details-drawer", {
              detail: { areaId, levelId, projectId },
            })
          );
        }

        setVisible(false);
      } else {
        console.warn("[In-App] Missing data in notification:", notification);
        navigate(notification.url);
      }
    } catch (error) {
      console.error("[In-App] Error parsing notification data:", error);
      navigate(notification.url);
    }
  };

  return (
    <>
      <button onClick={() => setVisible(!visible)}>
        {hasUnreadNotification ? (
          <>
            {color === "black" ? (
              <img src="/images/bell_red_black.png" alt="Notification" />
            ) : (
              <img src="/images/bell_red.png" alt="Notification" />
            )}
          </>
        ) : (
          <Bell color={color} />
        )}
      </button>
      {visible && (
        <Drawer
          expanded={visible}
          position="start"
          mode="overlay"
          animation={{ duration: 400 }}
          className="customDrawer transition-all duration-500 top-12"
        >
          <div className="max-w-[400px] shadow-lg top-12 flex flex-col bg-white fixed right-0 bottom-0 w-full">
            <div className="px-4">
              <div className="py-4 border-b flex items-center justify-between">
                <h2 className="font-medium text-lg">Notifications</h2>
                <Button
                  fillMode="flat"
                  className="p-2"
                  onClick={() => {
                    console.log("VISIBLE: ", visible);
                    setVisible(false);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-12">
              {notifications.length > 0 ? (
                <ul>
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onDelete={handleDelete}
                      onClick={handleNotificationItemClick} // ✅ Pass click handler
                    />
                  ))}
                </ul>
              ) : (
                <div className="py-12 flex items-center justify-center flex-col gap-3">
                  <BellOff
                    strokeWidth={1}
                    size={24}
                    className="text-primaryMain"
                  />
                  <p className="text-sm text-neutral-130">No Notifications</p>
                </div>
              )}
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
};

export default Alert;
